import { useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Divider, Text, Loader, Tooltip } from '@mantine/core'
import { Check, X, Pencil, Trash2 } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnDrawer from 'src/components/Core/DnDrawer'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import CarrierDirectoryLink from 'src/components/VoE/CarrierDirectoryLink/CarrierDirectoryLink'
import OutreachPreviewLine from 'src/components/VoE/OutreachPreviewLine/OutreachPreviewLine'
import { useEmploymentDetailsDrawerContext } from 'src/context/EmploymentDetailsDrawerContext'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import { useCompanyOutreachConfig } from 'src/hooks/useCompanyOutreachConfig'
import { outreachPreview } from 'src/lib/voe/outreachPreview'

import {
  FormTextField,
  FormMonthYearField,
  FormPhoneField,
  BooleanQuestion,
  FormAddressField,
  FormEquipmentTypesField,
  FormCompanyField,
} from './components'
import {
  FormCompanyDotField,
  FormContactField,
} from './components/FormComponents'
import KnownContactsBlock from './components/KnownContactsBlock'
import { useEmploymentExperience } from './hooks/useEmploymentExperience'

/** Statuses the outreach chain never sends on. */
const CLOSED_VOE_STATUSES: VoEStatus[] = [
  VoEStatus.COMPLETED,
  VoEStatus.CANCELED,
  VoEStatus.DECLINED,
]

const EmploymentDetailsDrawer = () => {
  const { opened, close, employmentExperienceId } =
    useEmploymentDetailsDrawerContext()

  // Editing state
  const [isEditing, setIsEditing] = useState(false)

  // Delete modal state
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)

  // Use the new employment experience hook
  const {
    employmentExperience,
    form,
    loading,
    queryLoading,
    handleSave,
    handleCancel,
    handleDelete,
    handleCompanySelect,
    handleContactSelected,
    isFormValid,
    knownContacts,
    handleUseContact,
    useContactSaving,
    contactHintProps,
    directoryCarrierId,
  } = useEmploymentExperience(employmentExperienceId, close)

  const handleClose = () => {
    handleCancel()
    setIsEditing(false)
    close()
  }

  // Don't render if we don't have the required data yet
  const blockedWarning = (contactType: 'EMAIL' | 'PHONE' | 'FAX') =>
    employmentExperience?.suppressedContactTypes?.includes(contactType)
      ? "Contact opted out — this won't be used for verifications."
      : undefined

  // Read mode only: an entry with no contact at all, a DOT to look it up by,
  // and at least one reachable contact held for that DOT. An entry that already
  // has a contact gets nothing extra
  const storedDotNumber = employmentExperience?.dotNumber?.trim() || ''
  const entryHasContact = [
    employmentExperience?.contactName,
    employmentExperience?.email,
    employmentExperience?.phone,
  ].some((value) => Boolean(value?.trim()))
  const showKnownContacts =
    !isEditing &&
    !employmentExperience?.isUnemployment &&
    !entryHasContact &&
    Boolean(storedDotNumber) &&
    knownContacts.length > 0

  // Edit mode only, and only on an entry whose linked VoE the chain can still
  // act on: contact edits flow to that VoE. No VoE yet, a VoE already closed,
  // or "Can we contact the employer?" set to No all mean automation will not
  // send anything, so the line would be a false promise there.
  const { config: outreachConfig } = useCompanyOutreachConfig()
  const linkedVoe = employmentExperience?.verificationOfEmployment
  const chainCanAct =
    Boolean(linkedVoe?.voeId) &&
    !CLOSED_VOE_STATUSES.includes(linkedVoe?.status as VoEStatus) &&
    form.values.shouldContactEmployer !== false
  const hasText = (value: unknown) =>
    typeof value === 'string' && value.trim().length > 0
  const preview =
    isEditing && !employmentExperience?.isUnemployment && chainCanAct
      ? outreachPreview(outreachConfig, {
          hasEmail: hasText(form.values.email),
          hasPhone: hasText(form.values.phone),
          isCurrentEmployer: !form.values.endDate,
        })
      : null

  // Once per edit session: the first time the line appears
  const previewTone = preview?.tone
  const previewTrackedRef = useRef(false)
  useEffect(() => {
    if (!isEditing) {
      previewTrackedRef.current = false
      return
    }
    if (!previewTone || previewTrackedRef.current) return
    previewTrackedRef.current = true
    datadogRum.addAction('voe_outreach_preview_shown', {
      surface: 'dashboard',
      component: 'EmploymentDetailsDrawer',
      trigger: outreachConfig?.outreachTrigger,
      tone: previewTone,
      voe_id: linkedVoe?.voeId,
    })
  }, [
    isEditing,
    previewTone,
    outreachConfig?.outreachTrigger,
    linkedVoe?.voeId,
  ])

  if (!employmentExperience || queryLoading) {
    return (
      <DnDrawer
        opened={opened}
        onClose={handleClose}
        stackId="employment-details-drawer"
      >
        <div className="flex h-screen items-center justify-center">
          <Loader size="md" />
        </div>
      </DnDrawer>
    )
  }

  return (
    <DnDrawer
      opened={opened}
      onClose={handleClose}
      stackId="employment-details-drawer"
    >
      <div className="flex h-screen flex-col space-y-4 pb-14">
        {/* HEADER */}
        <div className="flex flex-row items-center justify-between">
          <Text size="lg" fw={500}>
            {employmentExperience?.isUnemployment
              ? 'Unemployment Period'
              : (form.values.companyName as string) || 'Employment Details'}
          </Text>
          <div className="flex items-center gap-2">
            {!isEditing && !loading && (
              <>
                <DNButton
                  variant="transparent"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil size={12} />
                  Edit
                </DNButton>
                <DNButton
                  variant="transparent"
                  onClick={() => setDeleteModalOpened(true)}
                  className="text-doubleNickel-error-500"
                >
                  <Trash2 size={12} />
                  Delete
                </DNButton>
              </>
            )}
            {isEditing && !loading && (
              <>
                <Tooltip
                  label={
                    !isFormValid
                      ? 'Please fix form errors before saving'
                      : 'Save changes'
                  }
                  position="bottom"
                  disabled={isFormValid}
                >
                  <DNButton
                    variant="transparent"
                    onClick={() => {
                      handleSave()
                      setIsEditing(false)
                    }}
                    disabled={loading || !isFormValid}
                    className="text-doubleNickel-success-500"
                  >
                    <Check size={14} />
                    Save
                  </DNButton>
                </Tooltip>
                <DNButton
                  variant="transparent"
                  onClick={() => {
                    handleCancel()
                    setIsEditing(false)
                  }}
                  disabled={loading}
                  className="text-doubleNickel-error-500"
                >
                  <X size={14} />
                  Cancel
                </DNButton>
              </>
            )}
            {loading && <Loader size={12} color="gray" />}
          </div>
        </div>
        <Divider variant="dashed" />

        {/* BODY */}
        <div className="flex h-screen flex-col space-y-4 overflow-auto">
          {employmentExperience?.isUnemployment ? (
            // UNEMPLOYMENT DETAILS
            <>
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                UNEMPLOYMENT DETAILS
              </Text>
              <div className="space-y-3">
                <FormMonthYearField
                  label="Start Date"
                  editing={isEditing}
                  formProps={{
                    ...form.getInputProps('startDate'),
                    maxDate: form.values.endDate
                      ? new Date(form.values.endDate as string | number)
                      : new Date(),
                  }}
                />
                <FormMonthYearField
                  label="End Date"
                  editing={isEditing}
                  formProps={{
                    ...form.getInputProps('endDate'),
                    minDate: form.values.startDate
                      ? new Date(form.values.startDate as string | number)
                      : undefined,
                    maxDate: new Date(),
                  }}
                />
                <FormTextField
                  label="Unemployment Reason"
                  editing={isEditing}
                  formProps={form.getInputProps('unemploymentReason')}
                />
              </div>
            </>
          ) : (
            <>
              {/* COMPANY INFORMATION SECTION */}
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                COMPANY INFORMATION
              </Text>
              <div className="space-y-3">
                <FormCompanyField
                  label="Company Name"
                  required
                  editing={isEditing}
                  formProps={form.getInputProps('companyName')}
                  onSelect={handleCompanySelect}
                />
                <FormCompanyDotField
                  label="DOT Number"
                  editing={isEditing}
                  formProps={form.getInputProps('dotNumber')}
                  onSelect={handleCompanySelect}
                  readModeActions={
                    <CarrierDirectoryLink carrierId={directoryCarrierId} />
                  }
                />
                <FormAddressField
                  label="Address"
                  editing={isEditing}
                  formProps={form.getInputProps('address')}
                />
                <FormPhoneField
                  label="Fax Number"
                  editing={isEditing}
                  formProps={form.getInputProps('faxNumber')}
                  warning={blockedWarning('FAX')}
                />
              </div>
              <Divider variant="dashed" />

              {/* EMPLOYMENT QUESTIONS SECTION */}
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                CONTACT INFORMATION
              </Text>
              <div className="space-y-3">
                {showKnownContacts && (
                  <KnownContactsBlock
                    dotNumber={storedDotNumber}
                    contacts={knownContacts}
                    onUse={handleUseContact}
                    saving={useContactSaving}
                  />
                )}
                <FormContactField
                  dotNumber={form.values.dotNumber as string}
                  label="Contact Name"
                  editing={isEditing}
                  formProps={form.getInputProps('contactName')}
                  onSelect={handleContactSelected}
                  {...contactHintProps}
                />
                <FormTextField
                  label="Email"
                  editing={isEditing}
                  formProps={form.getInputProps('email')}
                  warning={blockedWarning('EMAIL')}
                />
                <FormPhoneField
                  label="Phone"
                  editing={isEditing}
                  formProps={form.getInputProps('phone')}
                  warning={blockedWarning('PHONE')}
                />
                <OutreachPreviewLine preview={preview} />
              </div>
              <Divider variant="dashed" />
              {/* JOB INFORMATION SECTION */}
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                JOB INFORMATION
              </Text>
              <div className="space-y-3">
                <FormTextField
                  label="Position"
                  required
                  editing={isEditing}
                  formProps={form.getInputProps('position')}
                />
                <FormMonthYearField
                  label="Start Date"
                  required
                  editing={isEditing}
                  formProps={{
                    ...form.getInputProps('startDate'),
                    maxDate: form.values.endDate
                      ? new Date(form.values.endDate as string | number)
                      : new Date(),
                  }}
                />
                <FormMonthYearField
                  label="End Date"
                  required
                  editing={isEditing}
                  formProps={{
                    ...form.getInputProps('endDate'),
                    minDate: form.values.startDate
                      ? new Date(form.values.startDate as string | number)
                      : undefined,
                    maxDate: new Date(),
                  }}
                />
                <FormTextField
                  label="Reason for Leaving"
                  required
                  editing={isEditing}
                  formProps={form.getInputProps('reasonForLeaving')}
                />
                <FormEquipmentTypesField
                  label="Equipment Types"
                  editing={isEditing}
                  formProps={form.getInputProps('equipmentTypes')}
                />
                <BooleanQuestion
                  label="Was this driver terminated?"
                  editing={isEditing}
                  formProps={{
                    value: form.values.isDriverTerminated?.toString(),
                    onChange: (value) =>
                      form.setFieldValue(
                        'isDriverTerminated',
                        value === 'true'
                      ),
                  }}
                />
                <BooleanQuestion
                  label="Was the driver subject to FMCSRs while employed?"
                  editing={isEditing}
                  formProps={{
                    value: form.values.isDriverSubjectToFMCSRs?.toString(),
                    onChange: (value) =>
                      form.setFieldValue(
                        'isDriverSubjectToFMCSRs',
                        value === 'true'
                      ),
                  }}
                />
                <BooleanQuestion
                  label="Was the job designated as a safety-sensitive function under DOT?"
                  editing={isEditing}
                  formProps={{
                    value:
                      form.values.isJobDesignatedAsSafetySensitiveFunctionUnderDOT?.toString(),
                    onChange: (value) =>
                      form.setFieldValue(
                        'isJobDesignatedAsSafetySensitiveFunctionUnderDOT',
                        value === 'true'
                      ),
                  }}
                />
                <BooleanQuestion
                  label="Can we contact the employer?"
                  editing={isEditing}
                  formProps={{
                    value: form.values.shouldContactEmployer?.toString(),
                    onChange: (value) =>
                      form.setFieldValue(
                        'shouldContactEmployer',
                        value === 'true'
                      ),
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        title={`Delete ${
          employmentExperience?.isUnemployment
            ? 'Unemployment Period'
            : 'Employment Experience'
        }`}
        description={`Are you sure you want to delete this ${
          employmentExperience?.isUnemployment
            ? 'unemployment period'
            : 'employment experience'
        }? This action cannot be undone.`}
        opened={deleteModalOpened}
        close={() => setDeleteModalOpened(false)}
        onDelete={handleDelete}
        stackId="delete-employment-modal"
      />
    </DnDrawer>
  )
}

export default EmploymentDetailsDrawer
